/**
 * 日落橙模版 - 注册页
 * 
 * 设计核心理念：左右分栏（优势展示+注册表单）
 * 与teal（步骤向导）和red（顶部Banner+大表单）都不同：
 * - 左侧：大块图文优势展示区（为什么选择我们）
 * - 右侧：紧凑注册表单卡片
 * - 暖色氛围，但不过度强调渐变背景
 * - 注重注册转化引导
 */

import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function SunsetRegisterPage() {
  const navigate = useNavigate();
  const { siteId } = useSite();
  const { siteConfig } = useSiteConfig();

  const [formData, setFormData] = useState({
    username: "", password: "", confirmPassword: "",
    nickname: "", phone: "", email: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.username.trim() || !formData.password.trim()) {
      setError("请填写用户名和密码");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }
    if (formData.password.length < 6) {
      setError("密码长度至少6位");
      return;
    }
    if (!agreeTerms) {
      setError("请先阅读并同意服务协议");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          nickname: formData.nickname || formData.username,
          phone: formData.phone,
          email: formData.email,
          site_id: siteId,
        }),
      });
      const result = await response.json();
      if (result.code !== 0) {
        setError(result.message || "注册失败，请重试");
      } else {
        setSuccess("注册成功！正在跳转到登录页...");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err: any) {
      setError(err?.message || "网络错误，请重试");
    } finally {
      setSubmitting(false);
    }
  };

  const advantages = [
    { icon: "ri-file-list-3-line", title: "免费发布信息", desc: "无需付费，直接发布您的分类信息" },
    { icon: "ri-eye-line", title: "海量流量曝光", desc: "覆盖本地用户，获得更多关注" },
    { icon: "ri-vip-crown-line", title: "VIP专属特权", desc: "置顶推荐，获得优先展示机会" },
    { icon: "ri-shield-check-line", title: "安全可靠", desc: "信息审核机制，保障真实可靠" },
  ];

  return (
    <div className="min-h-screen flex bg-[#fffbf5]">
      {/* ── 左侧优势展示区 ── */}
      <div className="hidden lg:flex w-5/12 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden items-center">
        {/* 装饰 */}
        <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-amber-200/20 blur-3xl" />
        <div className="absolute bottom-10 right-10 w-60 h-60 rounded-full bg-orange-200/20 blur-3xl" />

        <div className="relative z-10 max-w-md mx-auto px-8 py-12">
          {/* Logo */}
          <div className="mb-10">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-amber-500/20">
                {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
          </div>

          {siteName ? (
            <h2 className="text-3xl font-extrabold text-gray-800 mb-3">
              加入<span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent"> {siteName}</span>
            </h2>
          ) : (
            <h2 className="text-3xl font-extrabold text-gray-800 mb-3">
              加入<span className="bg-gradient-to-r from-amber-500 to-orange-600 bg-clip-text text-transparent">我们</span>
            </h2>
          )}
          <p className="text-gray-500 mb-10 leading-relaxed text-sm">
            注册账户，开启您的分类信息之旅
          </p>

          {/* 优势列表 */}
          <div className="space-y-5">
            {advantages.map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-white shadow-sm border border-amber-100 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-amber-500 text-lg`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-700">{item.title}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-amber-200/50">
            <p className="text-xs text-gray-400">已有账号？</p>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-amber-600 font-semibold hover:text-amber-700 mt-1">
              立即登录 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>

      {/* ── 右侧注册表单 ── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-3xl shadow-xl shadow-amber-500/5 border border-amber-100/50 p-8 sm:p-10">
            {/* 移动端Logo */}
            <div className="lg:hidden mb-6 text-center">
              {logo ? (
                <img src={logo} alt="" className="h-10 mx-auto mb-2" />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 mx-auto flex items-center justify-center text-white text-xl font-bold shadow-lg shadow-amber-500/20">
                  {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
                </div>
              )}
              {siteName && <p className="text-sm text-gray-500 mt-2">{siteName}</p>}
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-1">创建账户</h2>
            <p className="text-sm text-gray-400 mb-8">填写以下信息完成注册</p>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            {success && (
              <div className="mb-5 p-3.5 bg-green-50 border border-green-100 rounded-xl text-sm text-green-600 flex items-center gap-2">
                <i className="ri-check-line" />{success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">用户名 *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="用于登录"
                    className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">昵称</label>
                  <input
                    type="text"
                    value={formData.nickname}
                    onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                    placeholder="显示名称"
                    className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">手机号</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="方便联系（选填）"
                  className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">电子邮箱</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="接收通知（选填）"
                  className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">密码 *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="至少6位"
                    className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">确认密码 *</label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="再次输入密码"
                    className="w-full px-4 py-3 bg-amber-50/30 border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 transition-all"
                  />
                </div>
              </div>

              {/* 协议 */}
              <div className="pt-2">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="w-4 h-4 rounded accent-amber-500 mt-0.5 shrink-0"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    我已阅读并同意
                    <Link to="/page/register-agreement" className="text-amber-600 hover:text-amber-700 mx-1">服务协议</Link>
                    和
                    <Link to="/page/privacy" className="text-amber-600 hover:text-amber-700 ml-1">隐私政策</Link>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-2xl hover:from-amber-600 hover:to-orange-700 transition-all shadow-lg shadow-amber-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm mt-2"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />注册中...
                  </span>
                ) : "创建账户"}
              </button>
            </form>

            <div className="mt-6 text-center flex items-center justify-between">
              <Link to="/" className="text-sm text-gray-400 hover:text-gray-500 inline-flex items-center gap-1">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
              <p className="text-sm text-gray-400">
                已有账号？{" "}
                <Link to="/login" className="text-amber-600 hover:text-amber-700 font-semibold">登录</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
